import { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput
} from '@mui/material';
import MUIFormControl from '@mui/material/FormControl';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { InputType } from 'utils/constants';
import { GenericCardProps } from 'models';
import { selectAjaxLoader } from 'features/app/appSelectors';
import { useAppSelector } from 'hooks/useAppStore';

export interface FormControlProps {
  captionLabel?: string;
  errorMessage?: string;
  helperText?: string;
  iconPrimary?: GenericCardProps['iconPrimary'];
  iconSecondary?: GenericCardProps['iconPrimary'];
  placeholder?: string;
  textPrimary?: string;
  textSecondary?: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
  onBlur?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
  disabled?: boolean;
  type?: string;
  endAdornment?: string;
  name: string;
  value: any;
  isMultiline?: boolean;
  autofocus?: boolean;
  maxLength?: number;
  captionLabelEndAdornment?: string;
}

// ==============================|| FORM CONTROL ||============================== //

const ErrorText = (errorMessage: string, name: string, t: TFunction) => (
  <FormHelperText error id={`${name}-error`}>
    {t(errorMessage)}
  </FormHelperText>
);

const HelperText = (helperText: string | undefined, name: string) =>
  helperText ? (
    <FormHelperText id={`${name}-reason`}>{helperText}</FormHelperText>
  ) : null;

const FormControl = ({
  captionLabel,
  errorMessage,
  helperText,
  placeholder,
  onChange,
  onBlur,
  name,
  value,
  disabled,
  type,
  isMultiline,
  endAdornment,
  autofocus = false,
  maxLength,
  captionLabelEndAdornment
}: FormControlProps) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const adornmentRef = useRef<any>(null);
  const typeRelatedProps = () => {
    const props = {
      type: type ?? 'text'
    };

    if (isMultiline) {
      Object.assign(props, {
        multiline: isMultiline,
        rows: 3
      });
    }

    if (type === InputType.PASSWORD) {
      Object.assign(props, {
        endAdornment: (
          <InputAdornment position='end' ref={adornmentRef}>
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              edge='end'
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      });

      props.type = showPassword ? 'text' : type;
    }

    return props;
  };

  const endAdornmentToUse = endAdornment ? (
    <InputAdornment position='end' ref={adornmentRef}>
      {endAdornment}
    </InputAdornment>
  ) : null;

  const labelWidthInPx: number = adornmentRef.current
    ? adornmentRef.current.offsetWidth + 28
    : 24;

  const operationInProgress = useAppSelector(selectAjaxLoader);

  return (
    <MUIFormControl fullWidth error={!!errorMessage?.length}>
      <InputLabel
        sx={{
          maxWidth: `calc(100% - ${labelWidthInPx}px)`,
          '&.MuiInputLabel-shrink': { maxWidth: '100%' }
        }}
      >
        {t(captionLabel ?? '').concat(` ${captionLabelEndAdornment || ''}`)}
      </InputLabel>
      <OutlinedInput
        id={name}
        name={name}
        value={value}
        onChange={(e) => onChange(e)}
        onBlur={(e) => !!onBlur && onBlur(e)}
        label={t(captionLabel ?? '').concat(
          ` ${captionLabelEndAdornment || ''}`
        )}
        placeholder={placeholder}
        endAdornment={endAdornmentToUse}
        error={!!errorMessage?.length}
        disabled={disabled ?? operationInProgress}
        autoFocus={autofocus}
        inputProps={{ maxLength: maxLength }}
        {...typeRelatedProps()}
      />
      {errorMessage?.length
        ? ErrorText(errorMessage, name, t)
        : HelperText(helperText, name)}
    </MUIFormControl>
  );
};

FormControl.defaultProps = {
  disabled: false
};

export default FormControl;
